import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HomeCourseContentModel } from 'src/app/core/HomeCourseContentModel';
import { TagModel } from 'src/app/core/models';
import { StateModel } from 'src/app/core/StateModel';
import { AdminService } from 'src/app/services/admin/admin.service';
import { AuthSessionService } from 'src/app/services/auth-session/auth-session.service';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { CourseService } from 'src/app/services/course/course.service';



/* Start Category Model Section */
interface EcbCategory {
  id: number;
  name: string;
  icon: string;
}
/* End Category Model Section */

/* Start Feature Model Section */
interface EcbFeature {
  id: number;
  title: string;
  description: string;
  icon: string;
}
/* End Feature Model Section */

/* Start Course Model Section */
interface EcbCourse {
  id: number;
  title: string;
  category: string;
  duration: string;
  price: number;
  nextDate: string;
  image: string;
}
/* End Course Model Section */

/* Start Industry Model Section */
interface EcbIndustry {
  id: number;
  name: string;
  icon: string;
  className: string;
}
/* End Industry Model Section */

/* Start Statistic Model Section */
interface EcbStatistic {
  id: number;
  value: string;
  label: string;
  icon: string;
}
/* End Statistic Model Section */

/* Start Journey Model Section */
interface EcbJourneyStep {
  id: number;
  stepNumber: number;
  title: string;
  description: string;
  icon: string;
}
/* End Journey Model Section */

/* Start Upcoming Course Model Section */
interface EcbUpcomingCourse {
  id: number;
  title: string;
  location: string;
  month: string;
  day: string;
}
/* End Upcoming Course Model Section */

/* Start News Model Section */
interface EcbNews {
  id: number;
  category: string;
  title: string;
  image: string;
}
/* End News Model Section */

/* Start Partner Logo Model Section */
interface EcbPartnerLogo {
  id: number;
  name: string;
  image: string;
}
/* End Partner Logo Model Section */




@Component({
  selector: 'app-public-home',
  templateUrl: './public-home.component.html',
  styleUrls: ['./public-home.component.scss']
})
export class PublicHomeComponent implements OnInit {

  // Store search term
  public searchTerm: string = '';

  // Store selected category
  public selectedCategory: string = '';

  // Store categories
  public categories: EcbCategory[] = [];

  // Store features
  public features: EcbFeature[] = [];

  // Store courses
  public courses: EcbCourse[] = [];

  // Store filtered courses
  public filteredCourses: EcbCourse[] = [];

  // Store industries
  public industries: EcbIndustry[] = [];

  // Store statistics
  public statistics: EcbStatistic[] = [];

  // Store journey steps
  public journeySteps: EcbJourneyStep[] = [];

  // Store upcoming courses
  public upcomingCourses: EcbUpcomingCourse[] = [];
 

  // Store partner logos
  public partnerLogos: EcbPartnerLogo[] = [];

  tags: TagModel[] = [];

  // Long-course search controls shown on the Home qualification card.
  public longCourseOptions: Array<{ courseId: number; courseCode: string; courseName: string; qualificationType: string }> = [];
  public selectedLongCourseId: number | null = null;
  public longCourseKeyword = '';

  // Control admin login modal
  showAdminLoginModal = false;

  // Store email log type
  emailLogType: string | null = null;

  // Store email log ID
  emailLogId: string | null = null;

  // Store login email
  loginEmail = '';

  // Store login password
  loginPassword = '';


  dashboardPositions = [
    'LEFT_TOP',
    'RIGHT_TOP',
    'BOTTOM_LEFT',
    'BOTTOM_RIGHT'
  ];

  dashboardIcons: { [key: string]: string } = {
    LEFT_TOP: 'bi bi-award',
    RIGHT_TOP: 'bi bi-patch-check',
    BOTTOM_LEFT: 'bi bi-journal-check',
    BOTTOM_RIGHT: 'bi bi-file-earmark-medical'
  };

  dashboardIconClasses: { [key: string]: string } = {
    LEFT_TOP: 'ecb-web-certificate-two-icon',
    RIGHT_TOP: 'ecb-web-certificate-three-icon',
    BOTTOM_LEFT: 'ecb-web-certificate-four-icon',
    BOTTOM_RIGHT: 'ecb-web-diploma-icon'
  };


  //store states list 
  statesList: StateModel[] = [];

  //store selected state code
  selectedQualificationStateCode: string = 'MEL';

  //store form state code
  selectedShortCourseStateCode: string = 'MEL';

  // Store home course content by ContentCode.
  public homeCourseContent: {
    [key: string]: HomeCourseContentModel
  } = {};
  constructor(private router: Router, private authSessionService: AuthSessionService, private route: ActivatedRoute, private courseService: CourseService, private authenticationService: AuthenticationService,
    private adminService: AdminService
  ) {

  }

  // Initialise home page
  ngOnInit(): void {

    // Read route query parameters
    this.route.queryParamMap.subscribe(params => {

      // Store email log state
      const state = params.get('state');

      // Store email log type
      const type = params.get('type');

      // Store email log ID
      const id = params.get('id');

      // Handle email log redirect
      if (state === 'EmailLog') {

        this.emailLogType = type;
        this.emailLogId = id;

        this.handleEmailLogRedirect();

      }

    });


    // Load partner logos
    this.getPartnerLogos();

    
    // Load upcoming courses
    this.getUpcomingCourses();

    // Load journey steps
    this.getJourneySteps();

    // Load statistics
    this.getStatistics();

    // Load categories
    this.getCategories();

    // Load feature list
    this.getFeaturesList();

    // Load courses
    this.getCourses();

    // Load industries
    this.getIndustries();

  
    // Search courses
    this.searchCourses();


    //execute get qualification list
    this.loadQualifications();

    //execute loading Home Course Content
    this.loadHomeCourseContent();

    //execute loading states
    this.loadStates();
  }


  loadStates(): void {

    this.adminService.getStatesService('ALL_ACTIVE').subscribe({
      next: (response: StateModel[]) => {
        this.statesList = response || [];
        this.loadDropdownUOCCourses();
        this.loadQualificationDropdown();
      },
      error: error => {
        console.error('Failed to load states:', error);
        this.statesList = [];
      }
    });

  }

  loadQualificationDropdown(){
      // Lightweight long-course dropdown options.
    this.courseService.getPublicCourseOptions(this.selectedQualificationStateCode).subscribe({
      next: options => this.longCourseOptions = options || [],
      error: () => this.longCourseOptions = []
    });

  }


  onQualificationStateChange(stateCode: string): void {
    this.selectedQualificationStateCode = stateCode;
    const selectedState = this.statesList.find(
      x => x.code === stateCode
    );

     this.loadQualificationDropdown();
  }

  onShortCourseStateChange(stateCode: string): void {

    this.selectedShortCourseStateCode = stateCode;

    const selectedState = this.statesList.find(
      x => x.code === stateCode
    );

    console.log('Selected State:', selectedState);

    this.loadDropdownUOCCourses();
  }

  // ============================================================
  // LOAD HOME COURSE CONTENT
  // ============================================================

  loadHomeCourseContent(): void {

    this.homeCourseContent = {};

    this.adminService.getHomeCourseContent().subscribe({

      next: (response: HomeCourseContentModel[]) => {

        if (response && response.length) {

          response.forEach(item => {

            const code = item.contentCode?.trim().toUpperCase();

            if (code && item.isActive) {
              this.homeCourseContent[code] = item;
            }

          });

        }

      },

      error: error => {

        console.error(
          'Failed to load home course content:',
          error
        );

      }

    });

  }

  loadQualifications(): void {

    this.tags = [];

    this.courseService.getTags('PUBLIC_HOME_GRID').subscribe({

      next: (response: TagModel[]) => {

        if (response && response.length) {

          this.tags = response
            .map(tag => ({
              ...tag,
              dashboard: tag.dashboard?.trim() || null
            }))
            .filter(tag =>
              tag.dashboard !== null &&
              this.dashboardPositions.includes(tag.dashboard)
            )
            .sort((a, b) =>
              this.dashboardPositions.indexOf(a.dashboard!) -
              this.dashboardPositions.indexOf(b.dashboard!)
            );

        }

      },

      error: error => {
        console.error('Failed to load dashboard tags:', error);
      }

    });

  }


  // Admin login
  adminLogin(): void {

    // Validate login details
    if (!this.loginEmail || !this.loginPassword) {
      return;
    }

    // Create login request
    const loginRequest = {
      email: this.loginEmail,
      password: this.loginPassword
    };

    this.authenticationService
      .adminLogin(this.loginEmail, this.loginPassword)
      .subscribe({

        next: (response: any) => {

          // Check access token
          if (response?.accessToken) {

            // Store access token
            localStorage.setItem(
              'adminAccessToken',
              response.accessToken
            );

            // Store admin user
            localStorage.setItem(
              'adminUser',
              JSON.stringify(response)
            );

            // Hide login modal
            this.showAdminLoginModal = false;

            // Redirect to email log page
            this.redirectToEmailLogPage();
          }

        },

        error: (error) => {

          console.error(
            'Admin login failed',
            error
          );

        }

      });
  }


  // Handle email log redirect
  private handleEmailLogRedirect(): void {

    // Check login state
    const isLoggedIn =
      this.authSessionService.isLoggedIn();

    // Check admin role
    const isAdmin =
      this.authSessionService.hasRole('ADMIN');

    // Redirect logged-in admin
    if (
      isLoggedIn &&
      isAdmin
    ) {

      this.redirectToEmailLogPage();

      return;
    }

    // Show admin login modal
    this.showAdminLoginModal = true;
  }


  // Redirect to email log page
  private redirectToEmailLogPage(): void {

    // Redirect to enquiry list
    if (this.emailLogType === 'Enq') {

      this.router.navigate(
        ['/admin/enquiriesList'],
        {
          queryParams: {
            id: this.emailLogId
          }
        }
      );

      return;
    }

    // Redirect to booking list
    if (this.emailLogType === 'Book') {

      this.router.navigate(
        ['/admin/bookingsList'],
        {
          queryParams: {
            id: this.emailLogId
          }
        }
      );

    }

  }


  // Load partner logos
  getPartnerLogos() {

    this.partnerLogos = [
      {
        id: 1,
        name: 'BHP',
        image: 'assets/images/logo/bhp.webp'
      },
      {
        id: 2,
        name: 'Lendlease',
        image: 'assets/images/logo/Logo_Login_Lendlease.webp'
      },
      {
        id: 3,
        name: 'McConnell Dowell',
        image: 'assets/images/logo/mc_don.webp'
      },
      {
        id: 4,
        name: 'Downer',
        image: 'assets/images/logo/Downer_Group.webp'
      },
      {
        id: 5,
        name: 'CIMIC',
        image: 'assets/images/logo/cimic.webp'
      },
      {
        id: 6,
        name: 'Rio Tinto',
        image: 'assets/images/logo/riologo.webp'
      },
      {
        id: 7,
        name: 'Ventia',
        image: 'assets/images/logo/ventia-logo.webp'
      },
      {
        id: 8,
        name: 'Acciona',
        image: 'assets/images/logo/logo-acciona.webp'
      }
    ];
  }

 


  // Format date for API
  formatDateForApi(
    date: Date
  ): string {

    // Store year
    const year =
      date.getFullYear();

    // Store month
    const month =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        '0'
      );

    // Store day
    const day =
      String(
        date.getDate()
      ).padStart(
        2,
        '0'
      );


    // Return formatted date
    return (
      `${year}-${month}-${day}`
    );
  }


  // Load upcoming courses
  getUpcomingCourses(): void {

    // Store current date
    const today =
      new Date();

    // Store end date
    const endDate =
      new Date();

    // Set next 60 days
    endDate.setDate(
      today.getDate() + 60
    );


    // Format start date
    const startDateString =
      this.formatDateForApi(
        today
      );

    // Format end date
    const endDateString =
      this.formatDateForApi(
        endDate
      );


    // Load upcoming courses
    this.courseService
      .getXCourseCalendarList(
        startDateString,
        endDateString
      )
      .subscribe({

        next: (response: any[]) => {

          console.log(
            'Upcoming Course Response:',
            response
          );


          // Store current date
          const currentDate =
            new Date();

          // Reset current time
          currentDate.setHours(
            0,
            0,
            0,
            0
          );


          // Build upcoming course list
          this.upcomingCourses =
            (response || [])

              // Keep upcoming courses
              .filter(
                (item: any) => {

                  // Validate course start date
                  if (
                    !item?.occurance?.startDate
                  ) {
                    return false;
                  }


                  // Store course date
                  const courseDate =
                    new Date(
                      item.occurance.startDate
                    );

                  // Reset course time
                  courseDate.setHours(
                    0,
                    0,
                    0,
                    0
                  );


                  return (
                    courseDate >=
                    currentDate
                  );
                }
              )


              // Sort by nearest date
              .sort(
                (
                  a: any,
                  b: any
                ) => {

                  // Store first course date
                  const dateA =
                    new Date(
                      a?.occurance
                        ?.startDate
                    ).getTime();

                  // Store second course date
                  const dateB =
                    new Date(
                      b?.occurance
                        ?.startDate
                    ).getTime();


                  return (
                    dateA -
                    dateB
                  );
                }
              )


              // Keep first four courses
              .slice(
                0,
                4
              )


              // Map upcoming course details
              .map(
                (item: any) => {

                  // Store course date
                  const courseDate =
                    new Date(
                      item?.occurance
                        ?.startDate
                    );


                  return {

                    id:
                      item?.occurance?.id
                      || item?.course?.id,

                    title:
                      item?.course
                        ?.courseTitle
                      || '-',

                    location:
                      item?.occurance
                        ?.location
                      || '-',

                    month:
                      courseDate
                        .toLocaleString(
                          'en-AU',
                          {
                            month:
                              'short'
                          }
                        )
                        .toUpperCase(),

                    day:
                      String(
                        courseDate
                          .getDate()
                      )
                        .padStart(
                          2,
                          '0'
                        )
                  };
                }
              );


          console.log(
            'Upcoming Courses:',
            this.upcomingCourses
          );

        },


        error: (error) => {

          console.error(
            'Unable to load upcoming courses:',
            error
          );


          // Reset upcoming courses
          this.upcomingCourses =
            [];

        }

      });
  }

  // Load journey steps
  getJourneySteps() {

    this.journeySteps = [
      {
        id: 1,
        stepNumber: 1,
        title: 'Choose Course',
        description: 'Find the right course for your career.',
        icon: 'bi bi-journal-text'
      },
      {
        id: 2,
        stepNumber: 2,
        title: 'Book Online',
        description: 'Book instantly online in just a few clicks.',
        icon: 'bi bi-calendar-check'
      },
      {
        id: 3,
        stepNumber: 3,
        title: 'Attend Training',
        description: 'Learn from experienced industry trainers.',
        icon: 'bi bi-person-video3'
      },
      {
        id: 4,
        stepNumber: 4,
        title: 'Assessment',
        description: 'Complete practical and theory assessments.',
        icon: 'bi bi-clipboard-data'
      },
      {
        id: 5,
        stepNumber: 5,
        title: 'Certificate Issued',
        description: 'Receive your nationally recognised certificate.',
        icon: 'bi bi-patch-check'
      },
      {
        id: 6,
        stepNumber: 6,
        title: 'Career Ready',
        description: 'Use your qualification to find opportunities.',
        icon: 'bi bi-search'
      }
    ];
  }


  // Load statistics
  getStatistics() {

    this.statistics = [
      {
        id: 1,
        value: '25+',
        label: 'Years Experience',
        icon: 'bi bi-people'
      },
      {
        id: 2,
        value: '40+',
        label: 'National Courses',
        icon: 'bi bi-clipboard-check'
      },
      {
        id: 3,
        value: '15,000+',
        label: 'Students Trained',
        icon: 'bi bi-person-check'
      },
      {
        id: 4,
        value: '98%',
        label: 'Customer Satisfaction',
        icon: 'bi bi-patch-check'
      }
    ];
  }


  // Load categories
  getCategories() {

    this.categories = [
      {
        id: 1,
        name: 'Construction',
        icon: 'bi bi-cone-striped'
      },
      {
        id: 2,
        name: 'Mining',
        icon: 'bi bi-truck-front'
      },
      {
        id: 3,
        name: 'Civil',
        icon: 'bi bi-building-gear'
      },
      {
        id: 4,
        name: 'High Risk',
        icon: 'bi bi-exclamation-triangle'
      },
      {
        id: 5,
        name: 'First Aid',
        icon: 'bi bi-briefcase'
      },
      {
        id: 6,
        name: 'Online',
        icon: 'bi bi-display'
      },
      {
        id: 7,
        name: 'Refresher',
        icon: 'bi bi-clock-history'
      }
    ];
  }


  // Load features
  getFeaturesList() {

    this.features = [
      {
        id: 1,
        title: 'Nationally Accredited',
        description: 'All courses are nationally recognised and compliant.',
        icon: 'bi bi-award'
      },
      {
        id: 2,
        title: 'Experienced Trainers',
        description: 'Industry experts with real-world experience.',
        icon: 'bi bi-people'
      },
      {
        id: 3,
        title: 'Flexible Delivery',
        description: 'Online, onsite or in-class training options.',
        icon: 'bi bi-calendar3'
      },
      {
        id: 4,
        title: 'Fast Certification',
        description: 'Get certified quickly and start working.',
        icon: 'bi bi-lightning-charge-fill'
      },
      {
        id: 5,
        title: 'Australia Wide',
        description: 'Training locations throughout Australia.',
        icon: 'bi bi-geo-alt-fill'
      },
      {
        id: 6,
        title: 'Corporate Training',
        description: 'Customised training programs for your team.',
        icon: 'bi bi-person-workspace'
      }
    ];
  }


  // Load courses
  getCourses() {

    this.courses = [
      {
        id: 1,
        title: 'Operate elevating work platform',
        category: 'High Risk',
        duration: '1 Day',
        price: 220,
        nextDate: '24 August 2026',
        image: 'assets/images/PC-1.webp'
      },
      {
        id: 2,
        title: 'Nationally Recognised Forklift Training ',
        category: 'Construction',
        duration: '2 Day',
        price: 350,
        nextDate: '22 August 2026',
        image: 'assets/images/PC-2.webp'
      },
      {
        id: 3,
        title: 'Plant Machinery Training (Level 01)',
        category: 'High Risk',
        duration: '1/2 Day',
        price: 380,
        nextDate: '23 August 2026',
        image: 'assets/images/PC-3.webp'
      },
      {
        id: 4,
        title: 'Erect, alter and dismantle scaffolding',
        category: 'Construction',
        duration: '5 Days',
        price: 1500,
        nextDate: '21 August 2026',
        image: 'assets/images/PC-4.webp'
      },
      {
        id: 5,
        title: 'Perform LVR and Provide CPR',
        category: 'First Aid',
        duration: '1/2 Day',
        price: 160,
        nextDate: '20 August 2026',
        image: 'assets/images/PC-5.webp'
      }
    ];
  }


  // Load industries
  getIndustries() {

    this.industries = [
      {
        id: 1,
        name: 'Mining',
        icon: 'bi bi-truck-front',
        className: 'ecb-web-industry-navy'
      },
      {
        id: 2,
        name: 'Construction',
        icon: 'bi bi-cone-striped',
        className: 'ecb-web-industry-orange'
      },
      {
        id: 3,
        name: 'Civil',
        icon: 'bi bi-building',
        className: 'ecb-web-industry-blue'
      },
      {
        id: 4,
        name: 'Resources',
        icon: 'bi bi-leaf',
        className: 'ecb-web-industry-green'
      },
      {
        id: 5,
        name: 'Transport',
        icon: 'bi bi-truck',
        className: 'ecb-web-industry-deep-blue'
      },
      {
        id: 6,
        name: 'Manufacturing',
        icon: 'bi bi-building-gear',
        className: 'ecb-web-industry-purple'
      },
      {
        id: 7,
        name: 'Government',
        icon: 'bi bi-bank',
        className: 'ecb-web-industry-teal'
      },
      {
        id: 8,
        name: 'Corporate',
        icon: 'bi bi-people',
        className: 'ecb-web-industry-orange'
      }
    ];
  }


  // Search courses
  public searchCourses(): void {

    // Store search keyword
    const keyword =
      this.searchTerm.trim().toLowerCase();

    // Filter courses
    this.filteredCourses =
      this.courses.filter((course: EcbCourse) => {

        // Check keyword match
        const matchesKeyword =
          keyword.length === 0 ||
          course.title.toLowerCase().includes(keyword) ||
          course.category.toLowerCase().includes(keyword);

        // Check category match
        const matchesCategory =
          this.selectedCategory.length === 0 ||
          course.category === this.selectedCategory;

        return matchesKeyword && matchesCategory;
      });
  }


  // Select category
  public selectCategory(categoryName: string): void {

    // Set selected category
    this.selectedCategory =
      this.selectedCategory === categoryName
        ? ''
        : categoryName;

    // Refresh filtered courses
    this.searchCourses();
  }


  // Clear course filters
  public clearCourseFilters(): void {

    // Reset search term
    this.searchTerm = '';

    // Reset selected category
    this.selectedCategory = '';

    // Reset filtered course list
    this.filteredCourses = [...this.courses];
  }


  // Track item by ID
  public trackById(
    index: number,
    item: { id: number }
  ): number {

    return item.id;
  }


  // Search long courses from the Home page and open the paged qualification list.
  searchLongCourses(): void {

    this.router.navigate(['/course-qualifications'], {
      queryParams: {
        view: 'list',
        courseId: this.selectedLongCourseId || null,
        keyword: this.longCourseKeyword?.trim() || null,
        state_code: this.selectedQualificationStateCode || 'MEL',
      }
    });
  }

  // View all courses
  viewAllCourses(): void {

    // Navigate to course qualifications
    this.router.navigate(
      ['/course-qualifications']
    );
  }


  // View all course dates
  viewAllCourseDates(): void {

    // Navigate to course calendar
    this.router.navigate(
      ['/course-calendar']
    );
  }

  //store short course keyword
  shortCourseKeyword: string = '';

  //search function of short courses
  /* searchShortCourses(): void {
    this.router.navigate(['/course-short'], {
      queryParams: {
        view: 'list',
        search: this.selectedCourseFilter || null,
        searchKey: this.shortCourseKeyword?.trim() || null
      }
    });
  } */

  searchShortCourses(): void {
    this.router.navigate(['/course-short'], {
      queryParams: {
        type: 'all_courses',
        search: this.selectedCourseFilter || null,
        searchKey: this.shortCourseKeyword?.trim() || null,
        state_code: this.selectedShortCourseStateCode
      }
    });
  }

  shortCourseOptions: any[] = [];
  selectedCourseFilter: string = '';

  loadDropdownUOCCourses(): void {
    this.adminService
      .getXCourseList('GET_ALL', this.selectedShortCourseStateCode)
      .subscribe({
        next: (response: any[]) => {
          this.shortCourseOptions = response || [];
        },
        error: (error) => {
          console.error(
            'Error loading UOC courses:',
            error
          );
        }
      });
  }

  //sanitize with plain text of qualification
  getPlainDescription(html: string): string {

    if (!html) {
      return '';
    }

    const element = document.createElement('div');

    element.innerHTML = html;

    const plainText = (element.textContent || element.innerText || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const maxLength = 60;

    return plainText.length > maxLength
      ? plainText.substring(0, maxLength).trimEnd() + '...'
      : plainText;
  }
}
